import type { RecallGoal } from '../settings-recall-agent/types';
import type {
  AudienceCount,
  AudiencePatient,
  BuilderStep,
  BuilderToken,
  FieldKey,
  Readback,
  RecallDraft,
  Suggestion,
} from './types';

/**
 * Short helper so fixtures read like the message does: anything matching a field
 * key becomes a chip, everything else is literal text.
 */
export function t(...parts: string[]): BuilderToken[] {
  const KEYS = new Set<string>([
    'first_name',
    'practice_name',
    'practice_phone',
    'booking_link',
    'month_due',
    'last_visit',
    'last_treatment',
    'provider',
    'years_with_practice',
    'unused_benefits',
  ]);
  return parts.map((p) => (KEYS.has(p) ? { kind: 'field', field: p as FieldKey } : { kind: 'text', text: p }));
}

/**
 * Suggestions per goal (backend §3.3), each with two live counts and **sorted by
 * patients no running recall already reaches** — which turns the list into *where
 * your uncovered patients are* rather than a menu of queries.
 *
 * **The second number is derived, never written here** (fix 23 §5.2). Written
 * down, it said *Due for hygiene · 412 · 412 not in a recall yet* one click away
 * from a list page showing *Hygiene — 6 month* running with those same 412.
 * `coveredBy` names the recalls already reaching these patients; `uncoveredFor()`
 * subtracts them from the same store the list page reads.
 */
export const SUGGESTIONS: Record<RecallGoal, Suggestion[]> = {
  book_appointment: [
    {
      id: 'due-hygiene',
      title: 'Due for hygiene',
      description:
        'Patients the PMS says are due for hygiene, who have been in within two years and are not already booked.',
      matches: 470,
      coveredBy: ['hygiene-6'],
    },
    {
      id: 'hygiene-overdue-3',
      title: 'Hygiene overdue by 3+ months',
      description: 'Patients whose hygiene recall has been overdue for three months or more.',
      matches: 143,
    },
    {
      id: 'fluoride-under-14',
      title: 'Fluoride due, under 14',
      description: 'Patients under 14 who are due for a fluoride treatment.',
      matches: 61,
    },
    {
      id: 'checkup-due',
      title: 'Check-up exam due',
      description: 'Patients due for a check-up exam.',
      matches: 226,
      coveredBy: ['annual-exam'],
    },
    {
      id: 'perio-due',
      title: 'Perio maintenance due',
      description: 'Perio maintenance patients who are due.',
      matches: 96,
      coveredBy: ['perio-3'],
    },
    {
      // Shown disabled with the reason rather than hidden.
      id: 'unused-benefits',
      title: 'Unused benefits over $300',
      description: 'Patients with more than $300 of unused insurance benefit this year.',
      matches: 0,
      unavailable: 'Needs insurance checks from the last 14 days',
    },
  ],
  reactivate_patient: [
    {
      id: 'lapsed-18-36',
      title: 'Not seen in 18 months to 3 years',
      description: 'Patients whose last visit was between 18 months and three years ago.',
      matches: 1496,
      coveredBy: ['lapsed-18'],
    },
    {
      id: 'lapsed-36',
      title: 'Not seen in over 3 years',
      description: 'Patients whose last visit was more than three years ago.',
      matches: 742,
    },
    {
      id: 'missed-never-rebooked',
      title: 'Cancelled or missed and never rebooked',
      description: 'Patients who cancelled or missed an appointment in the last six months and never rebooked.',
      matches: 88,
    },
    {
      id: 'hygiene-overdue-6',
      title: 'Hygiene overdue by 6+ months',
      description: 'Patients whose hygiene recall has been overdue for six months or more.',
      matches: 204,
    },
  ],
  increase_loyalty: [
    {
      id: 'birthday-today',
      title: 'Birthday today',
      description: 'Patients whose birthday is today.',
      matches: 4,
      coveredBy: ['birthday'],
    },
    {
      id: 'anniversary',
      title: 'Patient for 1, 5 or 10 years this week',
      description: 'Patients whose first visit was one, five or ten years ago this week.',
      matches: 11,
    },
  ],
  clinical_safety: [
    {
      id: 'perio-overdue-2',
      title: 'Perio maintenance overdue by 2+ months',
      description: 'Perio maintenance patients whose recall has been overdue for two months or more.',
      matches: 34,
    },
  ],
};

/**
 * The starting sequence a goal loads (backend §4.5). **A new recall does not
 * start empty** — an empty editor is where a practice stalls.
 */
export function startingSteps(goal: RecallGoal): BuilderStep[] {
  const mk = (
    id: string,
    day: number,
    channel: BuilderStep['channel'],
    mode?: BuilderStep['mode'],
  ): BuilderStep => ({ id, day, channel, ...(mode ? { mode } : {}) });

  if (goal === 'increase_loyalty') {
    return [
      {
        ...mk('s1', 0, 'text', 'template'),
        body: t('Happy birthday, ', 'first_name', '! From all of us at ', 'practice_name', '.'),
      },
    ];
  }
  if (goal === 'clinical_safety') {
    return [mk('s1', 0, 'text', 'template'), mk('s2', 3, 'ai_call', 'smart'), mk('s3', 7, 'human_call')];
  }
  if (goal === 'reactivate_patient') {
    return [
      mk('s1', 0, 'text', 'smart'),
      mk('s2', 7, 'email', 'smart'),
      mk('s3', 14, 'ai_call', 'smart'),
      mk('s4', 28, 'human_call'),
    ];
  }
  return [
    mk('s1', 0, 'text', 'template'),
    mk('s2', 5, 'email', 'smart'),
    mk('s3', 12, 'ai_call', 'smart'),
    mk('s4', 21, 'human_call'),
  ];
}

/** What the board draws once *Due for hygiene* has been built. */
export const HYGIENE_READBACK: Readback = {
  understood: [
    'Due for hygiene, according to the practice’s records',
    'Last visit within the past 2 years',
    'No appointment already booked',
  ],
  leftOut: [
    {
      phrase: 'good payers',
      reason: 'Recalls don’t pick patients by what they owe. Remove it, or describe who you mean another way.',
    },
  ],
  alwaysLeftOut:
    'patients who have died or are inactive, anyone who opted out of these messages, anyone with no mobile or email, and anyone who already has an appointment.',
  hasClinicalCriterion: false,
  hasDueCriterion: true,
};

/** 412 match → 387 can be contacted, with every one of the 25 accounted for. */
export const HYGIENE_COUNT: AudienceCount = {
  match: 412,
  contactable: 387,
  reasons: [
    { label: 'have had enough messages this fortnight', n: 11, staysIn: true },
    { label: 'opted out of these messages', n: 7, staysIn: false },
    { label: 'have no mobile or email', n: 4, staysIn: false },
    { label: 'heard from another agent today', n: 3, staysIn: true },
  ],
};

export const HYGIENE_PATIENTS: AudiencePatient[] = [
  {
    id: 'p1',
    name: 'Marguerite Oyelowo',
    meta: '54 · Dr. Saito',
    why: 'Due for hygiene · last visit 6 months ago',
    lastVisit: '14 Mar',
    due: 'Nov',
    contact: 'will',
    values: {
      first_name: 'Marguerite',
      practice_name: 'Alamo Heights Dental',
      practice_phone: '(210) 555-0147',
      booking_link: 'elva.ai/b/k7m2',
      month_due: 'November',
      last_visit: '14 March',
      provider: 'Dr. Saito',
    },
  },
  {
    id: 'p2',
    name: 'Devon Achebe',
    meta: '31 · Dr. Chen',
    why: 'Due for hygiene · last visit was a filling on #19',
    lastVisit: '22 Mar',
    due: 'Nov',
    contact: 'will',
    values: {
      first_name: 'Devon',
      practice_name: 'Alamo Heights Dental',
      practice_phone: '(210) 555-0147',
      booking_link: 'elva.ai/b/p3x9',
      month_due: 'November',
      last_visit: '22 March',
      last_treatment: 'a filling on #19',
      provider: 'Dr. Chen',
    },
  },
  {
    // Farsi-speaking, and the same patient Triage and the reminders previews
    // carry (amendment 25 §7). Her preview lays out right to left, the counter
    // shows Farsi's 70-character parts, and the AI call is skipped for her
    // because ELVA cannot call in Farsi yet — the text goes instead.
    id: 'p3',
    name: 'Maryam Rezaei',
    meta: '41 · Dr. Saito',
    why: 'Due for hygiene · didn’t answer two texts last time',
    lastVisit: '2 Apr',
    due: 'Dec',
    contact: 'will',
    language: 'fa',
    values: {
      first_name: 'مریم',
      practice_name: 'دندانپزشکی آلامو هایتس',
      practice_phone: '(210) 555-0147',
      booking_link: 'elva.ai/b/w4n1',
      month_due: 'دسامبر',
      last_visit: '۲ آوریل',
      provider: 'دکتر سایتو',
    },
    rendered:
      'سلام مریم — جرم‌گیری شما در دسامبر موعدش می‌رسد. با یک کلیک وقت بگیرید، یا اگر الان وقتش نیست بگذارید برای بعد.',
  },
  {
    id: 'p4',
    name: 'Callum Brightwater',
    meta: '28 · Dr. Okafor',
    why: 'Due for hygiene',
    lastVisit: '9 Apr',
    due: 'Dec',
    contact: 'later',
    values: { first_name: 'Callum', practice_name: 'Alamo Heights Dental', month_due: 'December' },
  },
  {
    id: 'p5',
    name: 'Ines Vasconcelos',
    meta: '45 · Dr. Saito',
    why: 'Due for hygiene',
    lastVisit: '17 Apr',
    due: 'Dec',
    contact: 'opted_out',
    values: { first_name: 'Ines', practice_name: 'Alamo Heights Dental', month_due: 'December' },
  },
  {
    id: 'p6',
    name: 'Theo Lindqvist',
    meta: '39 · hygiene',
    why: 'Due for hygiene · new patient last spring',
    lastVisit: '24 Apr',
    due: 'Dec',
    contact: 'will',
    // No first name on file — the row that proves the fallback machinery works.
    values: { practice_name: 'Alamo Heights Dental', month_due: 'December' },
  },
  // Rows 7–20. The card said *The first 20* over six of them (fix 23 §5.3), and
  // six rows also made the search in *See all* look broken. These carry only the
  // fields a list row shows; the worked previews stay on the first six.
  ...[
    ['Priya Raghunathan', '36 · Dr. Chen', '2 May', 'Dec', 'will'],
    ['Emmanuel Asare', '61 · Dr. Saito', '8 May', 'Dec', 'will'],
    ['Wren Kowalczyk', '24 · hygiene', '13 May', 'Dec', 'will'],
    ['Otto Brennan-Lisle', '48 · Dr. Okafor', '19 May', 'Jan', 'will'],
    ['Solveig Amundsen', '33 · Dr. Chen', '26 May', 'Jan', 'later'],
    ['Nkechi Balogun', '57 · hygiene', '2 Jun', 'Jan', 'will'],
    ['Rafael Ybarra', '42 · Dr. Saito', '9 Jun', 'Jan', 'will'],
    ['Maeve Donnelly', '29 · Dr. Okafor', '16 Jun', 'Jan', 'will'],
    ['Hyun-woo Baek', '51 · Dr. Chen', '23 Jun', 'Jan', 'will'],
    ['Tamsin Ferreira', '38 · hygiene', '30 Jun', 'Feb', 'later'],
    ['Abdirahman Yusuf', '45 · Dr. Saito', '7 Jul', 'Feb', 'will'],
    ['Clementine Osei', '22 · Dr. Okafor', '14 Jul', 'Feb', 'will'],
    ['Lars Vandersteen', '66 · Dr. Chen', '21 Jul', 'Feb', 'will'],
    ['Junia Mbeki-Hart', '30 · hygiene', '28 Jul', 'Feb', 'will'],
  ].map(([name, meta, lastVisit, due, contact], i): AudiencePatient => ({
    id: `p${String(i + 7)}`,
    name: name ?? '',
    meta: meta ?? '',
    why: 'Due for hygiene',
    lastVisit: lastVisit ?? '',
    due: due ?? '',
    contact: (contact ?? 'will') as AudiencePatient['contact'],
    values: {
      first_name: (name ?? ' ').split(' ')[0] ?? '',
      practice_name: 'Alamo Heights Dental',
      month_due: due === 'Dec' ? 'December' : due === 'Jan' ? 'January' : 'February',
    },
  })),
];

/** The board's worked example, as a draft the three pages share. */
export const RECALL_DRAFT: RecallDraft = {
  name: 'Hygiene — 6 month',
  goal: 'book_appointment',
  description:
    'Patients the PMS says are due for hygiene, who have been in within two years, are not already booked, and are good payers.',
  readback: HYGIENE_READBACK,
  count: HYGIENE_COUNT,
  patients: HYGIENE_PATIENTS,
  leftOut: [],
  mentionBenefits: false,
  sendAlone: false,
  steps: [
    {
      id: 's1',
      day: 0,
      channel: 'text',
      mode: 'template',
      body: t(
        'Hi ',
        'first_name',
        ', you’re due for a cleaning at ',
        'practice_name',
        ' in ',
        'month_due',
        '. Book in one tap: ',
        'booking_link',
      ),
      fallbacks: { first_name: 'there' },
    },
    {
      id: 's2',
      day: 5,
      channel: 'email',
      mode: 'smart',
      subject: t('', 'first_name', ', your cleaning is due in ', 'month_due'),
      brief:
        'Remind them their cleaning is due and make booking feel like one small thing. Warm, short, no pressure. If they had a specific treatment last time, name it instead of saying “your last cleaning”.',
      mustInclude: ['month_due', 'practice_name', 'booking_link'],
      backup: t(
        'Hi ',
        'first_name',
        ', it’s time for your cleaning at ',
        'practice_name',
        '. You’re due in ',
        'month_due',
        ' — pick a time here: ',
        'booking_link',
      ),
      fallbacks: { first_name: 'there' },
    },
    {
      // Deliberately incomplete, so the fix list has something real to point at.
      id: 's3',
      day: 12,
      channel: 'ai_call',
      mode: 'smart',
      voicemail: 'leave_and_stop',
    },
    { id: 's4', day: 21, channel: 'human_call', callerNote: '' },
  ],
};

/**
 * Sample generated copy, so a Smart preview shows something the model would
 * write. **Every patient who can appear in a preview has one** — the third was
 * blank before, which read as *ELVA wrote nothing for Ruth* (spec 23 §3.3).
 *
 * `[Pick a time]` is gone from the words: a button is rendered as a button, and
 * a call has no button at all (§3.2).
 */
export const SMART_PREVIEW: Record<string, { subject?: string; body: string; spoken?: string }> = {
  p1: {
    subject: 'Marguerite, your cleaning is due in November',
    body: 'Hi Marguerite — you’re due for a cleaning in November at Alamo Heights Dental. Want me to find you a time?',
    spoken:
      'Hello, is that Marguerite? This is Alamo Heights Dental calling — you’re due for a cleaning in November, and I can find you a time now if that’s easier than ringing back.',
  },
  p2: {
    subject: 'Devon, your cleaning is due in November',
    body: 'Hi Devon — it’s been six months since we sorted that filling on #19, so you’re due a cleaning in November.',
    spoken:
      'Hello, is that Devon? Alamo Heights Dental here — it’s been about six months since we sorted that filling, so you’re due a cleaning in November. Shall I look at some times?',
  },
  p3: {
    // The English the practice wrote. The Farsi she actually receives is on her
    // own record, as `rendered` — the practice writes English, and ELVA sends
    // each patient theirs (§5).
    subject: 'Maryam, your cleaning is due in December',
    body: 'Hi Maryam — a quick one: your cleaning is due in December. One tap and it’s done, or ignore this and I’ll leave it.',
    spoken:
      'Hello, is that Maryam? This is Alamo Heights Dental — your cleaning is due in December. It’s a two-minute job to book, or I can leave it with you.',
  },
  p4: {
    subject: 'Callum, your cleaning is due in December',
    body: 'Hi Callum — you’re due for a cleaning in December at Alamo Heights Dental. Want me to find you a time?',
    spoken: 'Hello, is that Callum? Alamo Heights Dental — you’re due a cleaning in December. Shall I find you a time?',
  },
  p6: {
    subject: 'Your cleaning is due in December',
    body: 'Hi there — you’re due for a cleaning in December at Alamo Heights Dental. Want me to find you a time?',
    spoken: 'Hello — this is Alamo Heights Dental. You’re due a cleaning in December. Shall I find you a time?',
  },
};

/** What an AI call step opens with, when there is no brief yet to generate from. */
export const NO_BRIEF_YET = 'Add a brief to see how it reads';

/** How many patients lack each field, for the *If a detail is missing* rows. */
export const MISSING_COUNTS: Partial<Record<FieldKey, number>> = {
  first_name: 3,
  last_treatment: 168,
  provider: 22,
  last_visit: 9,
};

/**
 * What `/recall/new` starts from. **Empty** — the worked example below is the
 * seeded hygiene recall, and starting a new one from it is why every new recall
 * was called *Hygiene — 6 month* and arrived with four hygiene steps (spec 23
 * §1.1, §1.4).
 */
export const BLANK_DRAFT: RecallDraft = {
  name: '',
  description: '',
  patients: [],
  leftOut: [],
  steps: [],
  mentionBenefits: false,
  sendAlone: false,
};

/**
 * *Sent in the last hour* (spec 21 §4, fix 23 §4.3) — seeded, because an empty
 * list hid the one thing worth seeing: a failure carries **why** it failed, so a
 * wrong number reads as a wrong number rather than as a broken send.
 */
export interface TestSendRow {
  id: string;
  label: string;
  status: 'delivered' | 'failed';
  reason?: string;
  at: string;
}

export const TEST_SENDS: TestSendRow[] = [
  // Each row names its language (amendment 25 §7) — two rows that differ only by
  // language are otherwise indistinguishable.
  { id: 'ts1', label: 'Step 1 · text · English to (210) 555-0100', status: 'delivered', at: '14:06' },
  { id: 'ts2', label: 'Step 2 · email · English to amir@elva.ai', status: 'delivered', at: '14:07' },
  {
    id: 'ts3',
    label: 'Step 3 · AI call · Farsi to (210) 555-0100',
    status: 'failed',
    // Not a broken send: ELVA cannot call in Farsi yet, so the text version went.
    reason: 'no calls in Farsi yet — the text version was sent',
    at: '14:08',
  },
];
